import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Center, Spinner, SimpleGrid, Text, Flex, Field } from '@chakra-ui/react';
import { DataTable, InputField, SectionCard, type Column } from '@themes/aegix';
import { DrawerComponent } from '../../ui/drawerComponent';
import { Button as ButtonComponent } from '@themes/aegix';
import { useRooms, useCreateRoom } from '../../../hooks/queries/useRooms';
import { useUpdateFloor } from '../../../hooks/queries/useFloors';
import type { Floor, Room } from '../../../services';
import { RoomDrawer } from './RoomDrawer';
import { toaster } from '../../ui/toaster';

interface RoomRow {
  [key: string]: unknown;
  id: string;
  name: string;
  safe: string;
}

// columns moved inside

function transformRooms(rooms: Room[]): RoomRow[] {
  return rooms.map((r) => ({
    id: String(r.id),
    name: r.name,
    safe: r.safe || 'unknown',
  }));
}

interface FloorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  floor: Floor | null;
  selectedRoom: Room | null;
  onSelectRoom: (room: Room | null) => void;
}

export function FloorDrawer({
  isOpen,
  onClose,
  floor,
  selectedRoom,
  onSelectRoom,
}: FloorDrawerProps) {
  const { t } = useTranslation();
  const [floorName, setFloorName] = useState('');
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');

  const columns = useMemo<Column<RoomRow>[]>(() => [
    { key: 'name', label: t('sites.room', 'Room'), sortable: true, width: '200px' },
    { key: 'safe', label: t('sites.safeStatus', 'Safe Status'), sortable: true, width: '150px' },
  ], [t]);

  const { data, isLoading, error } = useRooms({
    floor: floor?.id,
    page_size: 50,
  });
  const createMutation = useCreateRoom();
  const updateFloorMutation = useUpdateFloor();

  const allRows = data ? transformRooms(data.results) : [];
  const rows = useMemo(() => {
    if (!search) return allRows;
    const q = search.toLowerCase();
    return allRows.filter((r) => r.name.toLowerCase().includes(q));
  }, [allRows, search]);

  useEffect(() => {
    if (floor) {
      setFloorName(floor.name);
      setDirty(false);
    }
  }, [floor]);

  const handleClose = useCallback(() => {
    onSelectRoom(null);
    onClose();
  }, [onClose, onSelectRoom]);

  const handleRowClick = useCallback(
    (row: RoomRow) => {
      const room = data?.results.find((r) => String(r.id) === row.id);
      if (room) {
        onSelectRoom(room);
      }
    },
    [data, onSelectRoom],
  );

  const handleAddRoom = useCallback(() => {
    if (!floor) return;
    createMutation.mutate(
      { id: 0, name: 'New Room', floor: String(floor.id) },
      {
        onSuccess: () => {
          toaster.create({ title: 'Room created', type: 'success', duration: 3000 });
        },
        onError: (err: unknown) => {
          toaster.create({
            title: 'Error',
            description: err instanceof Error ? err.message : 'Something went wrong',
            type: 'error',
            duration: 4000,
          });
        },
      },
    );
  }, [floor, createMutation]);

  const handleSaveFloor = useCallback(() => {
    if (!floor || !floorName.trim()) return;
    updateFloorMutation.mutate(
      { id: floor.id, data: { name: floorName.trim() } },
      {
        onSuccess: () => {
          toaster.create({ title: 'Floor updated', type: 'success', duration: 3000 });
          setDirty(false);
        },
        onError: (err: unknown) => {
          toaster.create({
            title: 'Error',
            description: err instanceof Error ? err.message : 'Something went wrong',
            type: 'error',
            duration: 4000,
          });
        },
      },
    );
  }, [floor, floorName, updateFloorMutation]);

  return (
    <>
      <DrawerComponent
        isOpen={isOpen}
        onClose={handleClose}
        title={floor ? `${t('sites.floor', 'Floor')} — ${floor.name}` : t('sites.floor', 'Floor')}
        size="md"
        closeOnOutside={false}
        closeOnEsc={false}
        footer={
          <SimpleGrid columns={1} gap={4} width="100%">
            <ButtonComponent visual="secondary" onClick={handleClose} data-e2e="close-floor-drawer-button">
              {t('common.close', 'Close')}
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <Flex direction="column" gap={4}>
          <SectionCard title={t('sites.floorDetails', 'Floor Details')}>
            <Field.Root>
              <Field.Label htmlFor="floor-name">{t('common.name', 'Name')}</Field.Label>
              <InputField
                id="floor-name"
                size="sm"
                value={floorName}
                onChange={(e) => { setFloorName(e.target.value); setDirty(true); }}
                data-e2e="floor-name-input"
              />
            </Field.Root>
            {dirty && (
              <Flex gap={2} justifyContent="flex-end" mt={3}>
                <ButtonComponent
                  visual="secondary"
                  size="sm"
                  onClick={() => {
                    if (floor) { setFloorName(floor.name); setDirty(false); }
                  }}
                >
                  {t('common.cancel', 'Cancel')}
                </ButtonComponent>
                <ButtonComponent
                  visual="primary"
                  size="sm"
                  onClick={handleSaveFloor}
                  disabled={updateFloorMutation.isPending}
                >
                  {updateFloorMutation.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                </ButtonComponent>
              </Flex>
            )}
          </SectionCard>

          <SectionCard
            title={t('sites.rooms', 'Rooms')}
            action={
              <ButtonComponent visual="primary" size="sm" onClick={handleAddRoom}>
                {t('sites.addRoom', 'Add Room')}
              </ButtonComponent>
            }
          >
            <Field.Root>
              <Field.Label htmlFor="filter-rooms" srOnly>
                {t('sites.filterRoomsLabel', 'Filter rooms')}
              </Field.Label>
              <InputField
                id="filter-rooms"
                size="sm"
                placeholder={t('sites.filterRooms', 'Filter rooms...')}
                aria-label={t('sites.filterRoomsLabel', 'Filter rooms')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                mb={3}
                data-e2e="filter-rooms-input"
              />
            </Field.Root>

            {isLoading ? (
              <Center py={8}>
                <Spinner size="lg" color="button.primary" />
              </Center>
            ) : error ? (
              <Center py={8}>
                <Text color="text.danger">{t('sites.failedLoadRooms', 'Failed to load rooms')}</Text>
              </Center>
            ) : rows.length === 0 ? (
              <Center py={8}>
                <Text color="text.secondary">{t('sites.noRoomsFound', 'No rooms found')}</Text>
              </Center>
            ) : (
              <DataTable
                columns={columns}
                data={rows}
                keyExtractor={(row) => row.id}
                onRowClick={handleRowClick}
              />
            )}
          </SectionCard>
        </Flex>
      </DrawerComponent>

      <RoomDrawer
        isOpen={!!selectedRoom}
        onClose={() => onSelectRoom(null)}
        room={selectedRoom}
      />
    </>
  );
}
